//import HttpsProxyAgent from 'https-proxy-agent'

import { mqttReconnectAttempts, ParcelType } from '../constants'
import mqtt, {
  type IClientOptions,
  type IClientPublishOptions,
  type MqttClient,
  type IPublishPacket,
  type IConnackPacket,
  type IClientSubscribeOptions,
  Packet,
  ISubscriptionGrant,
} from 'mqtt'

import { Parcel } from '@/common/classes'

/* --------------  MQTT Server --------------- */

/*const agent = new HttpsProxyAgent(proxyOpts)
const wsOptions = {
  agent,
  // other wsOptions
  // foo:'bar'
}
*/

const showConsoleLogs = import.meta.env.VITE_SHOW_LOGS || false

const MQTT_OPTIONS: IClientOptions = {
  host: import.meta.env.VITE_MQTT_HOST,
  hostname: import.meta.env.VITE_MQTT_HOST,
  protocol: import.meta.env.VITE_MQTT_PROTOCOL,
  port: import.meta.env.VITE_MQTT_PORT,
  path: '/talkwall_parcel',
  clientId: '',
  keepalive: 55,
  protocolId: 'MQTT',
  protocolVersion: 5,
  clean: true,
  reconnectPeriod: 5000, // time interval (in milliseconds) that the client will wait before attempting to reconnect to the broker after a disconnection
  connectTimeout: 30 * 1000, // maximum time the client will wait for a connection to the MQTT server to be established
  username: '',
  password: '',
  rejectUnauthorized: false,
}

const SUBSCRIPTION_OPTIONS: IClientSubscribeOptions = {
  qos: 2,
}

interface ServiceInterface {
  connected: () => boolean
  initializeClientConnection: ({
    userId,
    mqttUsername,
    mqttPassword,
    connectionCallback,
    newMessageCallback,
    catchCallback,
  }: {
    userId: string
    mqttUsername: string
    mqttPassword: string
    connectionCallback: (data?: IConnackPacket, error?: Error) => void
    newMessageCallback: (topic: string, payload: Buffer, packet: IPublishPacket) => void
    catchCallback: (message: string) => void
  }) => Promise<void>
  closeClientConnection: () => Promise<void>
  subscribe: (topic: string) => Promise<string[]>
  unSubscribe: (topic: string) => Promise<void>
  publish: (topic: string, data: string | Buffer) => Promise<void>
  clientId: string
}

function useMqttService(): ServiceInterface {
  const clientId = `_${Math.random().toString(16).substring(2, 8)}`
  let globalClient: MqttClient
  let mqttOptions: IClientOptions = {}
  let reconnectAttempts = 0

  const applyProxy = (options: IClientOptions): IClientOptions => {
    const host = import.meta.env.VITE_SERVER_HOST
    if (!host.includes('localhost')) {
      options.protocol = 'wss'
      options.path = '/talkwall_parcel'
      options.port = import.meta.env.VITE_MQTT_PORT
    }
    return options
  }

  const generateLastWillParcel = (userId: string): Buffer => {
    return Buffer.from(
      new Parcel({
        parcelType: ParcelType.UserDisconnect,
        subscription: {
          user: {
            id: userId,
            username: 'unknown - last will disconnect',
            isRegistered: false,
            isOwner: false,
          },
        },
      }).serialised(),
    )
  }

  const initializeClientConnection = ({
    userId,
    mqttUsername,
    mqttPassword,
    connectionCallback,
    newMessageCallback,
    catchCallback,
  }: {
    userId: string
    mqttUsername: string
    mqttPassword: string
    connectionCallback: (data?: IConnackPacket, error?: Error) => void
    newMessageCallback: (topic: string, payload: Buffer, packet: IPublishPacket) => void
    catchCallback: (message: string) => void
  }): Promise<void> => {
    return new Promise((resolve, reject) => {
      setOptions(mqttUsername, mqttPassword, userId)
      if (!globalClient) {
        const clt = mqtt.connect(mqttOptions)
        if (clt) {
          globalClient = clt
          applyClientRules(connectionCallback, newMessageCallback, catchCallback)
          resolve()
        } else reject()
      } else {
        globalClient.reconnect(mqttOptions)
        applyClientRules(connectionCallback, newMessageCallback, catchCallback)
        resolve() // resolve if client is already connected
      }
    })
  }

  const setOptions = (username: string, password: string, userId: string) => {
    mqttOptions = { ...MQTT_OPTIONS, username: username, password: password }
    mqttOptions = applyProxy(mqttOptions)
    mqttOptions.clientId = createNewClientId(userId)
    if (mqttOptions.will) {
      const lastWillPayload = generateLastWillParcel(userId)
      mqttOptions.will.payload = lastWillPayload
      mqttOptions.will.topic = 'disconnection'
      mqttOptions.will.qos = 2
      mqttOptions.will.retain = false
    }
  }

  const createNewClientId = (userId: string) => {
    return encodeURIComponent(userId) + `_${Math.random().toString(16).substring(2, 8)}`
  }

  const applyClientRules = (
    connectionCallback: (data?: IConnackPacket, error?: Error) => void,
    newMessageCallback: (topic: string, payload: Buffer, packet: IPublishPacket) => void,
    catchCallback: (message: string) => void,
  ) => {
    if (globalClient) {
      globalClient.on('connect', (packet: IConnackPacket): void => {
        if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: Connected to Message Broker at ${mqttOptions.host}`)
        reconnectAttempts = 0
        connectionCallback(packet)
      })

      globalClient.on('disconnect', (): void => {
        if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: Disconnected from Message Broker at ${mqttOptions.host}`)
      })

      globalClient.on('message', (topic: string, payload: Buffer, packet: IPublishPacket): void => {
        //if (showConsoleLogs) console.log(`Received packet with topic: ${topic}; ${packet.payload.toString()}`)
        newMessageCallback(topic, payload, packet)
      })

      globalClient.on('reconnect', async () => {
        if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: Re-connected to Message Broker`)
        reconnectAttempts += 1
        if (reconnectAttempts === mqttReconnectAttempts) {
          reconnectAttempts = 0
          if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: ENDING CONNECTION - RE-CONNECTION EXHAUSTED`)
          try {
            await closeClientConnection()
          } catch (error) {
            console.log(error)
          }
        } else connectionCallback()
      })

      globalClient.on('error', (error: Error) => {
        if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: Error: ${error.message}`)
        connectionCallback(undefined, error)
        //catchCallback(error.message) disable for now...
      })

      globalClient.on('close', () => {
        if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: Connection Closed`)
      })

      // client has lost connection to the MQTT broker
      globalClient.on('offline', () => {
        if (showConsoleLogs) console.log(`${new Date().toISOString()} MQTT: Connection Offline`)
      })
    } else console.error('No client available to listen to')
  }

  const connected = (): boolean => {
    return globalClient ? globalClient.connected : false
  }

  const closeClientConnection = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (globalClient)
        globalClient
          .endAsync(false, mqttOptions)
          .then(() => {
            if (showConsoleLogs) console.log('Disconnected from Message Broker')
            resolve()
          })
          .catch((error) => {
            if (error) reject(error)
          })
      else reject(new Error('No MQTT client available to close connection'))
    })
  }

  const subscribe = async (topic: string): Promise<string[]> => {
    let granted: ISubscriptionGrant[] = []
    try {
      granted = await globalClient.subscribeAsync(topic, SUBSCRIPTION_OPTIONS)
      if (granted.length === 0) {
        await unSubscribe(topic)
        // we try to unsubscribe and re-subscribe once before throwing an error
        granted = await globalClient.subscribeAsync(topic, SUBSCRIPTION_OPTIONS)
      }
    } catch (error) {
      console.log(new Error('No MQTT client available to subscribe to'))
    }
    return granted.map((g) => g.topic).filter((g) => g)
  }

  const unSubscribe = async (topic: string): Promise<void> => {
    try {
      await globalClient.unsubscribeAsync(topic, SUBSCRIPTION_OPTIONS)
    } catch (error) {
      console.log(new Error('No MQTT client available to unsubscribe from'))
    }
  }

  const publish = (topic: string, data: string | Buffer): Promise<void> => {
    const options: IClientPublishOptions = {
      qos: 2,
      properties: {
        contentType: 'application/json',
      },
      retain: false,
    }
    return new Promise((resolve, reject) => {
      if (globalClient)
        globalClient.publishAsync(topic, data, options).then((packet: Packet | undefined) => {
          if (!packet) reject(new Error('Unable to publish'))
          if (showConsoleLogs) console.log(`Data published on topic: ${topic}`)
          resolve()
        })
      else reject(new Error('No MQTT client available to publish to'))
    })
  }

  return {
    initializeClientConnection,
    closeClientConnection,
    subscribe,
    unSubscribe,
    publish,
    connected,
    clientId,
  }
}

export default useMqttService
